import { useEffect, useState } from "react";

import Alert from "@material-ui/lab/Alert";
import { Snackbar } from "@material-ui/core";
import { ProgressBar } from "../../shared/components/progressBar";
import { useStyles } from "./fileinput.styles";
import { validateFileName } from "./utils";

const getFileToDisplay = (file) => {
  if (file.includes("pdf")) return "/pdf-icon.png";
  if (file.includes("mp4")) return "/mp4-icon.png";
  if (file.includes("csv")) return "/csv-icon.png";
  if (file.includes("plain")) return "/text-icon.png";
  if (file.includes("vnd.ms-exce") || file.includes("spreadsheetml.sheet"))
    return "/excel-icon.png";
  if (file.includes("msword") || file.includes("wordprocessingml.document"))
    return "/word-icon.png";
  if (file.includes(".mp3") || file.includes(".mpeg")) return "/mp3-icon.png";

  return file;
};

interface FileInputProps {
  uploading?: boolean;
  maxSize?: number;
  cssClassName?: string;
  value?: string;
  onHandleChange?: (event: any) => void;
  error?: boolean;
  helperText?: string;
  name?: string;
  noimg?: any;
  clear?: boolean;
  placeholder?: string;
  height?: string;
  acceptFile?: string[];
  text?: string;
  inputProps?: any;
}

export const FileInput = ({
  uploading,
  maxSize = 20000000,
  cssClassName,
  value,
  onHandleChange,
  error,
  helperText,
  name,
  noimg,
  clear,
  placeholder,
  height,
  acceptFile,
  text,
  inputProps,
}: FileInputProps) => {
  const classes = useStyles();
  const [fileName, setFileName] = useState("");
  const otherInputProps = {};

  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState("");

  const setError = (message: string) => {
    setShowMessage(true);
    setMessage(message);
  };

  if (!!inputProps && !!inputProps["data-testid"]) {
    otherInputProps["data-testid"] = inputProps["data-testid"];
  }

  const [file, setFile] = useState(noimg);

  useEffect(() => {
    if (clear || noimg === placeholder) {
      if (noimg instanceof Blob || noimg instanceof File) {
        if (noimg.name.includes("image") || noimg.name.includes("png")) {
          setFileName(noimg.name);
          setFile(URL.createObjectURL(noimg));
        }
      } else {
        setFileName("");
        setFile(noimg);
      }
    }
  }, [clear, noimg, placeholder]);

  const handleChange = (event) => {
    const currentFile = event.target.files[0];
    const { size, name: _fileName, type } = currentFile;
    const isAcceptedFilesWereAssigned =
      Array.isArray(acceptFile) && acceptFile.length > 0;
    const isFileIntoAcceptedFiles =
      _fileName && isAcceptedFilesWereAssigned && acceptFile.includes(type);

    if (isFileIntoAcceptedFiles) {
      if (!validateFileName(_fileName)) {
        setError("Invalid file name, must contain only letters and/or numbers");
      } else if (size <= maxSize) {
        setFileName(_fileName);
        setFile(type);
        onHandleChange(currentFile);
      } else {
        setError("Maximum size exceeded");
      }
    } else {
      setError("Format not allowed");
    }
  };

  return (
    <div
      className={classes.fileWrapper}
      style={{
        height: height,
      }}
    >
      {uploading && <ProgressBar />}
      <label
        className={["MuiFormLabel-root", error ? "Mui-error" : ""].join(" ")}
        htmlFor="file"
      >
        {text}
      </label>
      <div
        className={classes.fileContainer}
        style={{
          height: height,
        }}
      >
        {file !== "/noimage.png" ? (
          <div className={classes.imageOverlay} />
        ) : null}
        {!file && <label htmlFor="file">{text}</label>}
        <img
          style={{
            position: "absolute",
            maxWidth: "100%",
            maxHeight: "100%",
            display: file === "/noimage.png" ? "none" : "block",
            zIndex: 30,
          }}
          src={getFileToDisplay(file)}
          alt={file === "/noimage.png" ? "No File Chosen" : ""}
        />
        <label className={classes.fileButton} htmlFor={`fileinput-${name}-id`}>
          Upload File
        </label>
        <input
          type="file"
          id={`fileinput-${name}-id`}
          name={name}
          className={cssClassName !== "" ? cssClassName : classes.fileInput}
          onChange={handleChange}
          accept={acceptFile.join(",")}
          value={value}
          {...otherInputProps}
        />
      </div>
      {fileName && (
        <div className={classes.fileNameAlign}>
          <label
            htmlFor={`fileinput-${name}-id`}
            className={[classes.fileNameColor, classes.fileNameTruncate].join(
              " "
            )}
          >
            {`File name: ${fileName}`}
          </label>
        </div>
      )}
      {error ? (
        <p className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error Mui-required">
          {helperText}
        </p>
      ) : null}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={showMessage}
        autoHideDuration={6000}
        onClose={() => {
          setShowMessage(false);
        }}
      >
        <Alert
          onClose={() => {
            setShowMessage(false);
          }}
          severity="error"
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  );
};
